import { appendScriptSrc, appendScriptInnerHtml } from 'utils/scripts';
import { loadedScriptsOnScroll, recaptchSrc, intercom } from 'define/scripts';
import { isRequestedFrom, getCountryCode } from 'utils/localisation';
import { getCookie, setCookie } from 'utils/cookie';
import { updateConsentStatus } from 'utils/consentManager';
import { handleAppendParams } from 'utils/analytics';
import { trackVisitor } from 'utils/visitorTracking';
import {
  CONSENT_COOKIE_NAME,
  getCookieOptions,
  BANNER_STORAGE_KEY,
  DEFAULT_CONSENT,
} from 'define/cookies';
import { euroMoneyRegion, ukRegion } from 'define/locale';
import { cookies as cookiesStore, storage } from 'store';
import { IS_KEYBOARD_STORAGE_KEY, KEYBOARD_NAVIGATION_KEYS } from 'define/accessibility';
require('assets/scss/app.scss');
require('lazysizes');
require('lazysizes/plugins/attrchange/ls.attrchange');
require('./static/visitor-id');
require('./static/entry-points');
require('./static/gclid');
require('./static/ads');
require('./static/referrer');

const appendScriptsOnScrollOptions = { once: true };

const isGdprCountry = () => {
  const country = getCountryCode();
  return !country || isRequestedFrom([...euroMoneyRegion, ...ukRegion]);
};

const getBannerTheme = lang => {
  const NON_GDPR_LANGUAGES = ['ae', 'az', 'kz'];
  const GDPR_LANGUAGES = ['de', 'pl', 'nl', 'fr', 'uk'];

  if (NON_GDPR_LANGUAGES.includes(lang)) return 'small';
  if (GDPR_LANGUAGES.includes(lang)) return 'gdpr';

  return isGdprCountry() ? 'gdpr' : 'small';
};

const shouldShowBanner = () => !localStorage.getItem(BANNER_STORAGE_KEY);

const initializeAutoConsent = () => {
  setCookie(CONSENT_COOKIE_NAME, JSON.stringify(DEFAULT_CONSENT), getCookieOptions());
  updateConsentStatus(DEFAULT_CONSENT);
  cookiesStore.setSeen(!shouldShowBanner());
};

const initializeGdprConsent = () => {
  cookiesStore.setSeen(false);
};

const initializeConsent = (bannerTheme, savedConsent) => {
  if (savedConsent) {
    const consent = JSON.parse(savedConsent);
    if (consent) {
      updateConsentStatus(consent);
      cookiesStore.setSeen(!shouldShowBanner());
      return;
    }
  }

  if (bannerTheme === 'small') {
    initializeAutoConsent();
  } else {
    initializeGdprConsent();
  }
};

export const onClientEntry = () => {
  const lang = process.env.GATSBY_LANG || 'en';
  const bannerTheme = getBannerTheme(lang);
  const savedConsent = getCookie(CONSENT_COOKIE_NAME);

  cookiesStore.setTheme(bannerTheme);
  initializeConsent(bannerTheme, savedConsent);
};

const appendScriptsOnScroll = () => {
  if (!isRequestedFrom(['ua', 'by', 'ru'])) {
    appendScriptInnerHtml(intercom.settings, 'body');
    appendScriptInnerHtml(intercom.script, 'body');
  }

  const captchaLoaded = document.getElementById('recaptcha');

  if (!captchaLoaded) {
    appendScriptSrc(recaptchSrc, 'body', 'recaptcha');
  }

  sessionStorage.setItem(loadedScriptsOnScroll, 'true');
};

const handleKeyboardInput = ({ key }) => {
  if (KEYBOARD_NAVIGATION_KEYS.includes(key)) {
    storage.addItem(IS_KEYBOARD_STORAGE_KEY, true);
  }
};

const handlePointerInput = () => {
  storage.addItem(IS_KEYBOARD_STORAGE_KEY, false);
};

export const onInitialClientRender = () => {
  window.addEventListener('scroll', appendScriptsOnScroll, appendScriptsOnScrollOptions);
  window.addEventListener('keydown', handleKeyboardInput, true);
  window.addEventListener('mousedown', handlePointerInput, true);
  window.addEventListener('touchstart', handlePointerInput, true);
  window.addEventListener('mousedown', handleAppendParams, true);
  window.addEventListener('keydown', handleAppendParams, true);
};

export const onRouteUpdate = ({ prevLocation }) => {
  trackVisitor({ isPageChange: Boolean(prevLocation) });

  const checkLoadedScripts = sessionStorage.getItem(loadedScriptsOnScroll);

  if (prevLocation && !checkLoadedScripts) {
    window.removeEventListener('scroll', appendScriptsOnScroll, appendScriptsOnScrollOptions);
    appendScriptsOnScroll();
  }
};

export const wrapPageElement = require(`./src/components/PageLayout`).default;
export const wrapRootElement = require(`./src/components/RootWrapper`).default;

export const disableCorePrefetching = () => true;
